import {
  frontmatterAliases,
  frontmatterTags,
  parseFrontmatter,
  updateFrontmatter,
  type MarkdownDocument,
} from '@markbird/shared';
import { dedupeTags } from './tags';
import { inlineTags } from './inlineTags';

/**
 * A document's metadata, and the rules for where it comes from.
 *
 * Tags and aliases belong in the file's own frontmatter: that is the only place
 * they survive being written to disk, opened in another editor, or synced as a
 * plain `.md`. The copies on the Dexie row are a *mirror* kept for indexing and
 * filtering, never the truth.
 *
 * Documents created before frontmatter existed still carry tags only on the row.
 * Rather than rewriting everyone's library in a migration, the rule is:
 *
 *   a document that declares tags in its frontmatter is governed by them;
 *   one that does not keeps its stored tags until something edits them.
 *
 * So migration happens the next time a document is written anyway, and no
 * document is silently modified. The same holds for aliases, which have no
 * legacy field at all and are therefore always frontmatter-only.
 */

type TagSource = Pick<MarkdownDocument, 'content' | 'tags'>;

/** True when the document declares tags of its own (even an empty list). */
export function declaresTags(content: string): boolean {
  const { data } = parseFrontmatter(content);
  return data.tags !== undefined || data.tag !== undefined;
}

/**
 * The tags that count for a document.
 *
 * Frontmatter's once it declares any, otherwise the mirror stored on the row —
 * plus any `#tag` written inline in the body. Both are real: the metadata block
 * is the tidy place to declare a tag, but nobody stops mid-sentence to go and
 * edit one, so inline tags are how notes actually get tagged.
 */
export function effectiveTags(doc: TagSource): string[] {
  const declared = declaresTags(doc.content) ? frontmatterTags(doc.content) : (doc.tags ?? []);
  return dedupeTags([...declared, ...inlineTags(doc.content)]);
}

/** Only the tags the metadata block declares — the ones an editor may rewrite. */
export function declaredTags(doc: TagSource): string[] {
  return dedupeTags(declaresTags(doc.content) ? frontmatterTags(doc.content) : (doc.tags ?? []));
}

/** Alternative titles this document answers to. Always frontmatter-only. */
export function effectiveAliases(doc: Pick<MarkdownDocument, 'content'>): string[] {
  return dedupeTags(frontmatterAliases(doc.content));
}

/** Content with `tags` written into its frontmatter (removed when empty). */
export function writeTags(content: string, tags: string[]): string {
  const clean = dedupeTags(tags);
  // `tag` is only ever read, but if a document uses it we must not leave a
  // stale copy behind that would fight the list we just wrote.
  const patch: Record<string, string[] | undefined> = { tags: clean };
  if (parseFrontmatter(content).data.tag !== undefined) patch.tag = undefined;
  return updateFrontmatter(content, patch);
}

/** Content with `aliases` written into its frontmatter (removed when empty). */
export function writeAliases(content: string, aliases: string[]): string {
  const clean = dedupeTags(aliases);
  const patch: Record<string, string[] | undefined> = { aliases: clean };
  if (parseFrontmatter(content).data.alias !== undefined) patch.alias = undefined;
  return updateFrontmatter(content, patch);
}

/**
 * The mirror fields to store alongside a document whenever it is written, so
 * queries never have to parse markdown.
 */
export function metadataMirror(doc: TagSource): { tags: string[]; aliases: string[] } {
  return { tags: effectiveTags(doc), aliases: effectiveAliases(doc) };
}
